export interface MountableApp {
    mount: (containerSelector: string) => unknown;
    unmount: () => void;
}

export interface MountLifecycleDeps<TOptions> {
    /** Marks the MFE context on the host bridge before the app boots. */
    setMfeContext: () => void;
    /** Creates the Vue app instance and runs async setup (stores, router, runtime). */
    createAndSetupApp: () => Promise<MountableApp>;
    /** Resolves when the MFE router has settled its initial navigation. */
    routerIsReady: () => Promise<void>;
    /** Writes host-provided options (company/brand/location) into the MFE stores. */
    applyHostOptions: (options?: TOptions) => void;
    /** Aligns the MFE router with the host router's current path. */
    syncRouterToHost: () => Promise<void>;
}

export const createMountLifecycle = <TOptions>(deps: MountLifecycleDeps<TOptions>) => {
    // Only ever points at a fully-mounted instance — never an in-flight one.
    let app: MountableApp | null = null;
    let containerId: string | null = null;
    let mountGeneration = 0;

    const mount = async (appContainerId: string, options?: TOptions): Promise<void> => {
        const generation = ++mountGeneration;

        if (app) {
            console.warn('[Auth MFE] App already exists. Forcing unmount...');
            app.unmount();
            app = null;
        }

        containerId = appContainerId;
        deps.setMfeContext();

        const instance = await deps.createAndSetupApp();
        await deps.routerIsReady();

        if (generation !== mountGeneration) {
            console.warn('[Auth MFE] mount() superseded while initializing — abandoning stale mount.');
            return;
        }

        deps.applyHostOptions(options);

        await deps.syncRouterToHost();

        if (generation !== mountGeneration) {
            console.warn('[Auth MFE] mount() superseded while syncing router — abandoning stale mount.');
            return;
        }

        instance.mount(appContainerId);
        app = instance;
    };

    const unmount = (): void => {
        // Bump before the early return so an in-flight mount() is invalidated even
        // when no app has finished mounting yet.
        mountGeneration++;

        if (!app) return;
        app.unmount();

        if (containerId) {
            const container = document.querySelector(containerId);
            if (container) container.innerHTML = '';
        }
        app = null;
    };

    return { mount, unmount };
};
